import { APP_ID, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BrowserModule } from '@angular/platform-browser';
import { Angulartics2Module } from 'angulartics2';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FlexLayoutModule } from '@angular/flex-layout';
import { ServiceWorkerModule } from '@angular/service-worker';
import { AvatarModule } from '../shared/standalone-packages/ngx-avatar-master/projects/ngx-avatar/src/public_api';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import {
    AppProvider,
    ClientMonitoringService,
    EcapCoreModule,
    EcapCorporateIdentityService,
    InterceptorModule,
    MultiTranslateHttpLoader,
    reducers,
    ShellDomainProvider,
    TransferHttpCacheModule
} from '../shared/standalone-packages/ecap3/core';
import { StoreModule } from '@ngrx/store';
import {
    FuseDirectivesModule,
    FuseMainModule,
    FuseNavigationModule
} from '../shared/standalone-packages/ecap3/template-atlas';
import { routes } from './routes';
import { UpdateService } from './service/updateSw';
import { TitleService } from './service/title.service';
import { DocumentUploadService } from '../sc-config/utils/document-upload.service';
import { SCPCXToolbarComponent } from './fuse/toolbar/toolbar.component';
import { FuseAboutComponentDynamicComponent } from './fuse/about-dynamic/about.component';
import { AboutDialogComponent } from './fuse/about/about-dialog.component';
import { RootDefaultComponent } from './components/root-default/root-default.component';
import { NotFoundComponent } from './components/not-found/not-found.component';
import { UnauthorizedComponent } from './components/unauthorized/unauthorized.component';
import { NotFoundSiteDomainComponent } from './components/site-domain/site-domain.component';
import { SecurityPermissionService } from '../sc-config/utils/security-permission.service';
import { PcxNavbarComponent } from './fuse/pcx-navbar/pcx-navbar.component';
import { AppUserManagementService } from '../app-user-management/service/app-user-management.service';

import { MAT_DATE_LOCALE } from '@angular/material/core';
import { ImageLoaderModule } from '../shared/modules/image-loader/image-loader.module';
import { SharedModule } from '../shared/shared.module';
import { MatMenuModule } from '@angular/material/menu';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { LoggerModule } from '../shared/modules/error-logging/logger.module';
import { ErrorInterceptor } from '../shared/interceptors/error.interceptor';
import { PlatformBreadcrumbSharedModule } from '../shared/standalone-packages/ecap3/app-platform-breadcrumb-shared-module';
import { GraphQLModule } from '../shared/standalone-packages/ecap3/app-platform-graphql';
import {
    PlatfromUserAccssManagementService,
    UAMProfileImageUploadService
} from '../shared/standalone-packages/ecap3/app-platform-user-access-management';
import { SignalrNotificationModule } from '../shared/standalone-packages/ecap3/signalr-notification';
import { MatBadgeModule } from '@angular/material/badge';
import { primeNgThemeConfig } from '../../style/prime-ng/prime-ng-theme';
import { TranslationBlocksResourceModel } from '../shared/models';
import { AppAutomationReportComponent } from '../app-automation-report/components/app-automation-report.component';
import { PerformanceTrackerService } from '../shared/services/performance-tracker.service';
import { IpexFileTokenInterceptor } from '../shared/interceptors/ipex-file-token.interceptor';

export function HttpLoaderFactory(http: HttpClient) {
    return new MultiTranslateHttpLoader(http, [
        // { prefix: './assets/i18n/generic-app/', suffix: '.json' },
        // { prefix: './assets/i18n/root/', suffix: '.json' }
        new TranslationBlocksResourceModel('generic-app'),
        new TranslationBlocksResourceModel('root')
    ]);
}

@NgModule({
    declarations: [
        NotFoundSiteDomainComponent,
        RootDefaultComponent,
        NotFoundComponent,
        UnauthorizedComponent,
        SCPCXToolbarComponent,
        AboutDialogComponent,
        FuseAboutComponentDynamicComponent,
        PcxNavbarComponent,
        AppAutomationReportComponent
    ],
    bootstrap: [RootDefaultComponent],
    // @ts-ignore
    imports: [
        StoreModule.forRoot(reducers),
        EcapCoreModule.forRoot(),
        SignalrNotificationModule,
        BrowserModule,
        BrowserAnimationsModule,
        PlatformBreadcrumbSharedModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient]
            },
            isolate: true
        }),
        InterceptorModule,
        FuseMainModule,
        TransferHttpCacheModule.forRoot(),
        MatSnackBarModule,
        CommonModule,
        FormsModule,
        MatIconModule,
        MatButtonModule,
        MatBadgeModule,
        FlexLayoutModule.withConfig({
            useColumnBasisZero: false,
            printWithBreakpoints: [
                'xs',
                'sm',
                'md',
                'lg',
                'xl',
                'lt-sm',
                'lt-md',
                'lt-lg',
                'lt-xl',
                'gt-xs',
                'gt-sm',
                'gt-md',
                'gt-lg'
            ]
        }),
        MatSidenavModule,
        RouterModule.forRoot(routes, {
            initialNavigation: ShellDomainProvider['api']?.initialNavigation ?? 'disabled'
        }),
        ServiceWorkerModule.register('/ngsw-worker.js', {
            enabled: ShellDomainProvider['api']?.initialNavigation?.production ?? true
        }),
        GraphQLModule,
        Angulartics2Module.forRoot({
            pageTracking: {
                excludedRoutes: ['login'],
                clearQueryParams: true,
                clearHash: true
            }
        }),
        MatDialogModule,
        MatIconModule,
        MatButtonModule,
        MatProgressBarModule,
        MatMenuModule,
        MatToolbarModule,
        AvatarModule,
        FuseNavigationModule,
        FuseDirectivesModule,
        ImageLoaderModule,
        SharedModule,
        MatTooltipModule,
        LoggerModule
    ],
    providers: [
        { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
        { provide: HTTP_INTERCEPTORS, useClass: IpexFileTokenInterceptor, multi: true },
        { provide: APP_ID, useValue: 'arc2' },
        SecurityPermissionService,
        { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
        UpdateService,
        DocumentUploadService,
        TitleService,
        AppUserManagementService,
        PlatfromUserAccssManagementService,
        UAMProfileImageUploadService,
        provideHttpClient(withInterceptorsFromDi()),
        ...primeNgThemeConfig.providers
    ]
})
export class RootBrowserModule {
    constructor(
        private appProvider: AppProvider,
        private ecapCorporateIdentityService: EcapCorporateIdentityService,
        private clientMonitoringService: ClientMonitoringService,
        private performanceTrackerService: PerformanceTrackerService
    ) {
        this.clientMonitoringService.init();
        this.appProvider.setCurrentApps(routes);
        this.ecapCorporateIdentityService.setTitle('ipex');
        try {
            if ((window.performance as any).memory || (console as any).memory) {
                setInterval(() => this.performanceTrackerService.trackMemoryUsage(), 5000);
            }
        } catch (error) {
            console.error('memory api not found');
        }
    }
}
